import React from 'react';
import { Alert, Button, theme } from 'antd';
import { Link } from 'react-router-dom';

import { useTrackBillingEvent, useSuggestedPlan, usePlanStatus } from '~features/billing';
import { useRenderUsagePercentage } from '../../../hooks/usePrerenderUser';
import { useTrial } from '../useTrial';

function useGetUpgradePlanLink() {
  const suggestedPlan = useSuggestedPlan();

  return React.useMemo(() => {
    if (!suggestedPlan) return `/billing/plans`;

    return `/billing/plans?selectedPlan=${suggestedPlan.id}`;
  }, [suggestedPlan]);
}

export type FreeTrialBannerVariant = 'renderUsageWarning' | 'lastDayWarning';

const ALERT_CONTENT_BY_VARIANT = {
  renderUsageWarning: {
    message: 'Careful! You’ve used up 90% of your renders. Upgrade your plan to maintain your progress.',
    ctaLocation: 'free trial header banner: 90% render used',
  },
  lastDayWarning: {
    message: 'Only one day left in your free trial! Upgrade your plan to maintain your progress.',
    ctaLocation: 'free trial header banner: 1 day left',
  },
} satisfies Record<FreeTrialBannerVariant, { message: string; ctaLocation: string }>;

export function useFreeTrialBannerVariant(): FreeTrialBannerVariant | null {
  const { daysLeftOnTrial } = useTrial();
  const planStatus = usePlanStatus();
  const renderUsagePercentage = useRenderUsagePercentage();

  if (planStatus === 'cancelled' || daysLeftOnTrial === null) {
    return null;
  }

  if (renderUsagePercentage >= 90 && renderUsagePercentage < 100 && daysLeftOnTrial > 1) {
    return 'renderUsageWarning';
  }

  if (daysLeftOnTrial > 0 && daysLeftOnTrial <= 1 && planStatus !== 'cancellation_scheduled') {
    return 'lastDayWarning';
  }

  return null;
}

function Banner() {
  const { token } = theme.useToken();
  const trackBillingEvent = useTrackBillingEvent();
  const bannerVariant = useFreeTrialBannerVariant();
  const upgradePlanLink = useGetUpgradePlanLink();

  const onLinkClicked = (cta_location: string, cta_name: string) => {
    trackBillingEvent('UpgradeClicked', {
      cta_location,
      cta_name,
    });
  };

  if (bannerVariant === null) {
    return null;
  }

  const alertContent = ALERT_CONTENT_BY_VARIANT[bannerVariant];

  return (
    <Alert
      style={{ margin: token.margin }}
      message={alertContent.message}
      type="warning"
      showIcon
      banner
      action={
        <Link to={upgradePlanLink} onClick={() => onLinkClicked(alertContent.ctaLocation, 'Upgrade my plan')}>
          <Button type="primary">Upgrade my plan</Button>
        </Link>
      }
    />
  );
}

export default Banner;
